import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  ListSubheader,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ROUTE_PATH from '../../../config/routes.config';
import Iconify from '../../common/iconify.component';
import {
  CONTENT_ALIAS,
  DEFAULT_CHANNEL,
  SHOW_ADMIN_USERS,
  SHOW_CONTENT_CATEGORIES,
  SHOW_CONTENT_VIDEOS,
  SHOW_OVERVIEW_DASHBOARD,
  SHOW_REPORTS_OVERVIEW_ADS,
  SHOW_REPORTS_OVERVIEW_VIDEOS,
  SHOW_TABS,
  SHOW_CONTACTS,
  SUBCONTENT_CATEGORIES,
  SHOW_LANGUAGES,
  SHOW_RESOURCES,
  SHOW_BULK_EMAIL,
  SHOW_REPORTS_ENGAGEMENT,
} from '../../../config/const.config';

const StyledSubHeader = styled((props) => <ListSubheader {...props} />)(
  ({ theme }) => ({
    background: 'transparent',
    color: theme.palette.text.primary,
  })
);

const StyledNavItem = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 40,
  position: 'relative',
  textTransform: 'capitalize',
  color: theme.palette.text.secondary,
  borderRadius: 30,
}));

const StyledNavItemIcon = styled(ListItemIcon)({
  width: 20,
  height: 20,
  color: 'inherit',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 20,
});

const NavItem = ({ title, path, icon, subNav, isCollapse }) => (
  <StyledNavItem
    component={RouterLink}
    to={path}
    sx={{
      '&.active': {
        color: '#212B36',
        bgcolor: '#F4F6F8',
        fontWeight: 600,
        '& .MuiListItemIcon-root': {
          color: '#212B36',
        },
      },
      height: subNav ? 34 : 40,
    }}
  >
    {!subNav && <StyledNavItemIcon>{icon}</StyledNavItemIcon>}
    {!isCollapse && (
      <ListItemText
        disableTypography
        primary={title}
        sx={{
          color: 'inherit',
          fontWeight: 'inherit',
          pl: subNav ? 6 : 0,
          overflowX: 'clip',
          textWrap: 'nowrap',
          textOverflow: 'ellipsis',
          paddingRight: 2,
        }}
      />
    )}
  </StyledNavItem>
);
NavItem.propTypes = {
  title: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.element,
  subNav: PropTypes.bool,
  isCollapse: PropTypes.bool,
};
NavItem.defaultProps = {
  icon: <div />,
  subNav: false,
  isCollapse: false,
};

const NavGroup = ({ title, routePath, icon, navGroup, isCollapse }) => {
  const { pathname } = useLocation();
  const isActive = useMemo(
    () => pathname.includes(routePath),
    [pathname, routePath]
  );
  const [open, setOpen] = useState(isActive);
  const handleClick = () => setOpen(!open);

  useEffect(() => {
    if (isCollapse) return;
    setOpen(isActive);
  }, [isActive, isCollapse]);

  return (
    <>
      <StyledNavItem
        onClick={handleClick}
        sx={
          isActive
            ? {
                color: '#212B36',
                bgcolor: '#F4F6F8',
                fontWeight: 600,
                height: 40,
              }
            : {
                height: 40,
              }
        }
      >
        <StyledNavItemIcon>{icon}</StyledNavItemIcon>
        {!isCollapse && (
          <ListItemText
            disableTypography
            primary={title}
            sx={{
              color: 'inherit',
              fontWeight: 'inherit',
              overflowX: 'clip',
              textWrap: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          />
        )}
        {!isCollapse && (
          <StyledNavItemIcon>
            <Iconify icon={open ? 'mdi:menu-up' : 'mdi:menu-down'} />
          </StyledNavItemIcon>
        )}
      </StyledNavItem>

      {!isCollapse && (
        <Collapse in={open} timeout="auto">
          <List disablePadding sx={{ p: 1 }}>
            {navGroup.map(
              (nav) =>
                nav.status === 1 && (
                  <NavItem
                    key={nav.name}
                    title={nav.name}
                    path={`${routePath}/${nav.slug}`}
                    icon={<div />}
                    subNav
                    isCollapse={isCollapse}
                  />
                )
            )}
          </List>
        </Collapse>
      )}
    </>
  );
};
NavGroup.propTypes = {
  title: PropTypes.string.isRequired,
  routePath: PropTypes.string.isRequired,
  icon: PropTypes.element.isRequired,
  navGroup: PropTypes.array.isRequired,
  isCollapse: PropTypes.bool,
};

NavGroup.defaultProps = {
  isCollapse: false,
};

const NavItems = ({ categoriesTab, isCollapse }) => (
  <Box>
    <List disablePadding sx={{ p: 1 }}>
      {SHOW_OVERVIEW_DASHBOARD && (
        <NavItem
          title="Overview Dashboard"
          path={ROUTE_PATH.DASHBOARD}
          icon={<Iconify icon="ic:round-dashboard" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_ADMIN_USERS && (
        <NavItem
          title="Users"
          path={ROUTE_PATH.ADMIN_USERS}
          icon={<Iconify icon="mdi:users-check" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_CONTACTS && (
        <NavItem
          title="Subscribers"
          path={ROUTE_PATH.CONTACTS}
          icon={<Iconify icon="material-symbols:contact-mail-outline" />}
          isCollapse={isCollapse}
        />
      )}

      {!isCollapse && (
        <StyledSubHeader component="div">Content</StyledSubHeader>
      )}
      {!DEFAULT_CHANNEL && (
        <NavItem
          title="Channels"
          path={ROUTE_PATH.CONTENT_CHANNELS}
          icon={<Iconify icon="ic:twotone-checklist" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_TABS && (
        <NavItem
          title="Tabs"
          path={ROUTE_PATH.CONTENT_TABS}
          icon={<Iconify icon="material-symbols:tab-group-outline" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_CONTENT_CATEGORIES && !SUBCONTENT_CATEGORIES && (
        <NavItem
          title={`${CONTENT_ALIAS.CATEGORY_PLURAL}`}
          path={ROUTE_PATH.CONTENT_CATEGORIES}
          icon={<Iconify icon="material-symbols:category-outline-rounded" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_CONTENT_CATEGORIES && SUBCONTENT_CATEGORIES && (
        <NavGroup
          title={`${CONTENT_ALIAS.CATEGORY_PLURAL}`}
          routePath={ROUTE_PATH.CONTENT_CATEGORIES}
          icon={<Iconify icon="material-symbols:category-outline-rounded" />}
          navGroup={categoriesTab}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_CONTENT_VIDEOS && (
        <NavItem
          title={`${CONTENT_ALIAS.VIDEO_PLURAL}`}
          path={ROUTE_PATH.CONTENT_VIDEOS}
          icon={<Iconify icon="octicon:video-16" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_CONTENT_VIDEOS && (
        <NavItem
          title="Ads"
          path={ROUTE_PATH.CONTENT_ADS}
          icon={<Iconify icon="mdi:advertisements" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_LANGUAGES && (
        <NavItem
          title="Languages"
          path={ROUTE_PATH.CONTENT_LANGUAGES}
          icon={<Iconify icon="mdi:language" />}
          isCollapse={isCollapse}
        />
      )}

      {(SHOW_BULK_EMAIL || SHOW_RESOURCES) && !isCollapse && (
        <StyledSubHeader component="div">Tools</StyledSubHeader>
      )}
      {SHOW_BULK_EMAIL && (
        <NavItem
          title="Bulk Email"
          path={ROUTE_PATH.BULK_EMAIL}
          icon={<Iconify icon="fluent:mail-multiple-32-regular" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_RESOURCES && (
        <NavItem
          title="Resources"
          path={ROUTE_PATH.RESOURCES}
          icon={<Iconify icon="mingcute:attachment-line" />}
          isCollapse={isCollapse}
        />
      )}

      {(SHOW_REPORTS_OVERVIEW_VIDEOS ||
        SHOW_REPORTS_OVERVIEW_ADS ||
        SHOW_REPORTS_ENGAGEMENT) &&
        !isCollapse && (
          <StyledSubHeader component="div">Analytics Reports</StyledSubHeader>
        )}
      {SHOW_REPORTS_OVERVIEW_VIDEOS && (
        <NavItem
          title="Overview (App)"
          path={ROUTE_PATH.REPORTS_OVERVIEW_VIDEOS}
          icon={<Iconify icon="ic:outline-analytics" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_REPORTS_OVERVIEW_ADS && (
        <NavItem
          title="Overview (Ads)"
          path={ROUTE_PATH.REPORTS_OVERVIEW_ADS}
          icon={<Iconify icon="ic:outline-analytics" />}
          isCollapse={isCollapse}
        />
      )}
      {SHOW_REPORTS_ENGAGEMENT && (
        <NavItem
          title="Engagement"
          path={ROUTE_PATH.REPORTS_ENGAGEMENT}
          icon={<Iconify icon="ic:outline-trending-up" />}
          isCollapse={isCollapse}
        />
      )}
    </List>
  </Box>
);

NavItems.propTypes = {
  categoriesTab: PropTypes.array.isRequired,
  isCollapse: PropTypes.bool,
};

NavItems.defaultProps = {
  isCollapse: false,
};

export default NavItems;
